import { SxProps, useTheme } from "@mui/material";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import type { ApplicationState } from "@store/index";
import { useProviderFiles } from "containers/hooks/useData";
import { Category, DataPoint } from "fileParsing/types";
import { useAppSelector } from "index";
import { ReactNode, useEffect, useRef, useState } from "react";
import { Theme } from "@mui/system";
import { Folder as FolderIcon } from "@mui/icons-material";
import WorldMap from "./WorldMap";

type ToolTipSettings = {
  tooltipWidth: number;
  margin: number;
};

export type TooltipTexts = {
  title: ReactNode;
  subtitle: ReactNode;
  date?: ReactNode;
  body: ReactNode;
  body2?: ReactNode;
};

type SettledProps = {
  settings: ToolTipSettings;
  visible: boolean;
  // texts: [ReactNode, ReactNode, ReactNode, ReactNode];
  texts: TooltipTexts;
  category: Category;
  iconType?: "datapoint" | "file" | "folder" | "disabledFolder" | "default";
  uri?: string;
  isFixed?: boolean;
  dataPoint?: DataPoint;
  clickToNext?: () => void;
  clickToPrev?: () => void;
};

type Props = SettledProps & {
  position: { x: number; y: number };
};

export function StationaryTooltip({
  visible,
  settings,
  texts,
  category,
  uri,
  iconType,
  isFixed,
  dataPoint,
}: SettledProps) {
  const { title, subtitle, date, body, body2 } = texts;
  const theme = useTheme();

  const providerFiles = useProviderFiles();
  const options = useAppSelector((state: ApplicationState) => state.options.options);
  const isPrivate = options.HideSensitiveData.value;

  function findDataUri(uri: string) {
    // search somewhere in "files.data.binaryData"
    let binaryData: string | undefined;
    for (const providerFile of providerFiles) {
      if (providerFile.path === uri && providerFile.binaryData) {
        binaryData = providerFile.binaryData;
        break;
      }
    }

    if (binaryData === undefined) {
      return null;
    }

    return (
      <a href={binaryData} target="_blank" rel="noopener noreferrer">
        <img
          src={binaryData}
          style={{
            maxWidth: "100%",
            maxHeight: "20vh",
            margin: "auto",
            filter: isPrivate ? "blur(20px)" : "inherit",
          }}
        />
      </a>
    );
  }

  const renderIcon = () => {
    switch (iconType) {
      case "file":
        return (
          <div
            style={{
              height: "2em",
              margin: "0 7px",
            }}
          >
            <svg width="30" height="40" viewBox="0 0 40 50" fill="#fff" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M0 5C0 2.23858 2.23858 0 5 0H26L40 18V45C40 47.7614 37.7614 50 35 50H5C2.23858 50 0 47.7614 0 45V5Z"
                fill={theme.palette.text.primary}
              />
              <path d="M26 0L40 18H26V0Z" fill="#00000044" />
            </svg>
          </div>
        );
      case "disabledFolder":
      case "folder":
        return (
          <div
            style={{
              height: "2em",
              width: "2em",
              margin: "0 7px",
            }}
          >
            <FolderIcon
              sx={{
                color: iconType === "folder" ? theme.palette.primary.light : theme.palette.grey[600],
                height: "1.4em",
                width: "1.4em",
              }}
            />
          </div>
        );
      case "default":
      case "datapoint":
      default:
        return (
          <div
            style={{
              height: "3em",
              width: "3em",
              margin: "0 0px",
            }}
          >
            <svg
              width="100%"
              height="100%"
              viewBox="0 0 24 24"
              strokeWidth="2"
              stroke={theme.palette.text.primary}
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path stroke="none" d="M0 0h24v24H0z" fill="none" />
              <path
                d="M12 7a5 5 0 1 1 -4.995 5.217l-.005 -.217l.005 -.217a5 5 0 0 1 4.995 -4.783z"
                strokeWidth="0"
                fill={theme.palette.text.primary}
              />
            </svg>
          </div>
        );
    }
  };

  return (
    <Box
      sx={{
        background: theme.palette.background.default,
        borderRadius: "15px 15px 5px 5px",
        overflowX: "scroll",
        boxShadow: theme.shadows[1],
        //color: '#e6e6e6', //"rgb(175, 180, 185)",
        border: isFixed ? "solid 1px #fff" : "",
        fontSize: "1em",
        overflowWrap: "break-word",
        whiteSpace: "break-spaces",
        maxWidth: `${settings.tooltipWidth}px`,
        minWidth: `${settings.tooltipWidth}px`,
        maxHeight: window.innerHeight,
        padding: 0,
        visibility: isFixed || visible ? "visible" : "hidden",
        opacity: isFixed || visible ? 1 : 0,
        transition: "opacity 0.3s, left 0.0s, top 0.0s",
      }}
    >
      <div
        style={{
          // background: theme.palette.background.tooltip,
          background: theme.palette.categories[category].main,
          margin: 0,
          padding: theme.spacing(1),
          display: "flex",
          gap: "0.5em",
          alignItems: "center",
        }}
      >
        {renderIcon()}

        <Box sx={{ color: theme.typography.body1, width: "100%" }}>
          <div
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography sx={{ fontSize: "1.2rem", fontWeight: 600 }}>{title}</Typography>

            <Typography sx={{ fontSize: "1rem", fontWeight: 400, textAlign: "end" }}>{date}</Typography>
          </div>
          <Typography
            sx={{
              color: isPrivate ? "transparent" : undefined,
              textShadow: isPrivate ? "0px 0px 4px white" : undefined,
            }}
          >
            {subtitle}
          </Typography>
        </Box>
      </div>
      <Box
        sx={{
          p: 1,
          maxHeight: "45vh",
        }}
      >
        {category === Category.Location && !!dataPoint && !!dataPoint.longlat && (
          <WorldMap data={[dataPoint as DataPoint]} size={{ width: "100%", height: "100%" }} />
        )}
        <Typography
          sx={{
            mb: 1.5,
            color: isPrivate ? "transparent" : undefined,
            textShadow: isPrivate ? "0px 0px 6px white, 0px 0px 6px white, 0px 0px 10px white" : undefined,
          }}
        >
          {body}
        </Typography>
        {uri && findDataUri(uri)}
        {body2}
      </Box>
    </Box>
  );
}

function Tooltip({ visible, settings, position, texts, category, uri, iconType, isFixed, dataPoint }: Props) {
  const [height, setHeight] = useState(0);
  const [currentPosition, setCurrentPosition] = useState({
    x: position.x,
    y: position.y,
  });

  useEffect(() => {
    // When fixed changes to positive, save the current position
    // console.log('Updating pinned position to ', isFixed, posX, posY);
    if (!isFixed && position) setCurrentPosition(position);
  }, [isFixed, position]);

  // const posX = (isFixed ? isFixated.x : position?.x) ?? 0;
  // const posY = (isFixed ? isFixated.y : position?.y) ?? 0;
  const elementRef = useRef<HTMLElement>();

  const theme = useTheme();

  useEffect(() => {
    // When element renders, update the height dynamicly to adapt to content
    if (elementRef.current) setHeight(elementRef.current.clientHeight);
  }, [elementRef, texts, settings]);

  function getLeft() {
    return document.documentElement.clientWidth - (settings.tooltipWidth + settings.margin) < currentPosition.x
      ? currentPosition.x - settings.tooltipWidth - settings.margin
      : currentPosition.x;
  }

  function getTop() {
    return Math.min(document.documentElement.clientHeight - height - settings.margin, currentPosition.y);
  }

  return (
    <Box
      ref={elementRef}
      className="tooltip"
      sx={{
        position: "fixed",
        left: getLeft(),
        top: getTop(),
        zIndex: theme.zIndex.tooltip,
        pointerEvents: isFixed ? "auto" : "none",
      }}
    >
      <StationaryTooltip
        settings={settings}
        visible={visible}
        texts={texts}
        category={category}
        iconType={iconType}
        uri={uri}
        isFixed={isFixed}
        dataPoint={dataPoint}
      />
    </Box>
  );
}

type PlainTooltipProps = {
  position: { x: number; y: number };
  settings: ToolTipSettings;
  visible: boolean;
  sx?: SxProps<Theme>;
  children?: ReactNode;
};
export function PlainTooltip({ visible, settings, position, sx, children }: PlainTooltipProps) {
  const theme = useTheme();

  const [height, setHeight] = useState(0);

  const posX = position?.x ?? 0;
  const posY = position?.y ?? 0;
  const elementRef = useRef<HTMLElement>();

  useEffect(() => {
    // When element renders, update the height dynamically to adapt to content
    if (elementRef.current) setHeight(elementRef.current.clientHeight);
  }, [elementRef, children, settings]);

  function getLeft() {
    return document.documentElement.clientWidth - settings.tooltipWidth - settings.margin < posX
      ? document.documentElement.clientWidth - settings.tooltipWidth - settings.margin
      : posX;
  }

  function getTop() {
    return Math.min(document.documentElement.clientHeight - height - settings.margin, posY);
  }

  return (
    <Box
      ref={elementRef}
      className="tooltip"
      sx={{
        position: "fixed",
        left: getLeft(),
        top: getTop(),
        zIndex: theme.zIndex.tooltip,
        background: theme.palette.background.default,
        borderRadius: "15px 15px 5px 5px",
        boxShadow: theme.shadows[1],
        border: "solid 1px #fff",
        maxWidth: `${settings.tooltipWidth}px`,
        minWidth: `${settings.tooltipWidth}px`,
        maxHeight: window.innerHeight,
        padding: theme.spacing(1),
        visibility: visible ? "visible" : "hidden",
        opacity: visible ? 1 : 0,
        margin: settings.margin,
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

export default Tooltip;
